import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Observable } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
}

/**
 * Drop-in replacement for the pattern library's NeoBreadcrumbService, provided
 * under that token in app.config.ts so the `neo-breadcrumb` component renders
 * these crumbs unchanged (it only consumes `breadcrumbs$`).
 *
 * Why not the library service: it reads the *inherited* ActivatedRoute `data`.
 * Under Angular's default `emptyOnly` inheritance, componentless/empty-path
 * parent routes leak their `data.breadcrumb` onto children, which duplicates
 * crumbs in this app's deeply-grouped admin route tree (e.g. the componentless
 * `routing-rules` group leaks "Routing Rules" onto its `create` child).
 *
 * This service instead reads each route's OWN static config data
 * (`routeConfig.data.breadcrumb`), which is never inherited — so every crumb
 * appears exactly once, on the route that declares it, with no per-route
 * suppression. The URL is built from the accumulated path segments (WCAG 2.4.5).
 */
@Injectable({ providedIn: 'root' })
export class CmBreadcrumbService {
  private readonly _router = inject(Router);
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  /**
   * Explicit crumbs set by a page whose trail can't be derived from static route
   * config — e.g. detail pages that need an async label (the case number). Kept
   * until the next navigation, when it is cleared so it never leaks to other pages.
   */
  private _override: Breadcrumb[] | null = null;

  public readonly breadcrumbs$: Observable<Breadcrumb[]> = this._breadcrumbs$.asObservable();

  constructor() {
    this._rebuild();
    this._router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this._override = null;
        this._rebuild();
      });
  }

  /**
   * Override the route-derived trail with explicit crumbs (e.g. "Dashboard ›
   * Case CM-123"). Automatically cleared on the next navigation.
   */
  public setCrumbs(crumbs: Breadcrumb[]): void {
    this._override = crumbs;
    this._breadcrumbs$.next(crumbs);
  }

  private _rebuild(): void {
    if (this._override) {
      return;
    }

    const crumbs: Breadcrumb[] = [];
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = this._router.routerState.snapshot.root;

    while (node) {
      const baseLength = segments.length;
      segments.push(...node.url.map((segment) => segment.path));

      const raw = node.routeConfig?.data?.['breadcrumb'] as
        | string
        | (string | null)[]
        | null
        | undefined;

      if (typeof raw === 'string' && raw) {
        // Single crumb at this node's cumulative URL.
        this._addCrumb(crumbs, raw, '/' + segments.join('/'));
      } else if (Array.isArray(raw)) {
        // One label per path segment of THIS node — for flat multi-segment
        // routes like 'user-management/create' that have no intermediate route
        // node. `null` entries skip that segment. URLs are derived from the real
        // segments, so they stay correct across portals (cases/investigations).
        raw.forEach((label, index) => {
          const segmentIndex = baseLength + index;
          if (label && segmentIndex < segments.length) {
            this._addCrumb(crumbs, label, '/' + segments.slice(0, segmentIndex + 1).join('/'));
          }
        });
      }

      node = node.firstChild;
    }

    this._breadcrumbs$.next(crumbs);
  }

  /** Pushes a crumb, guarding against accidental consecutive duplicates. */
  private _addCrumb(crumbs: Breadcrumb[], label: string, url: string): void {
    const previous = crumbs[crumbs.length - 1];
    if (!previous || previous.label !== label || previous.url !== url) {
      crumbs.push({ label, url });
    }
  }
}
